import React, { Component } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import Highlight from 'react-highlighter';

import { trunc } from '../../../helpers';

const messages = defineMessages({
  CALL_BEGIN: {
    id: 'BillingCurrentMonth.CALL_BEGIN',
    defaultMessage: 'Gesprächsbeginn',
  },
  USER_NAME: {
    id: 'BillingCurrentMonth.USER_NAME',
    defaultMessage: 'Benutzer',
  },
  CUSTOMER_NAME: {
    id: 'BillingCurrentMonth.CUSTOMER_NAME',
    defaultMessage: 'Teilnehmername',
  },
  CUSTOMER_PHONE: {
    id: 'BillingCurrentMonth.CUSTOMER_PHONE',
    defaultMessage: 'Teilnehmer-TelNr.',
  },
  PROJECT: {
    id: 'BillingCurrentMonth.PROJECT',
    defaultMessage: 'Projekt',
  },
  DURATION: {
    id: 'BillingCurrentMonth.DURATION',
    defaultMessage: 'Dauer (Sek.)',
  },
  CALL_ACTIVE: {
    id: 'BillingCurrentMonth.CALL_ACTIVE',
    defaultMessage: 'Das Gespräch ist noch aktiv',
  },
  TOTAL_DURATION: {
    id: 'BillingCurrentMonth.TOTAL_DURATION',
    defaultMessage: 'Summe (in Sekunden)',
  },
  EMPTY_MESSAGE: {
    id: 'BillingCurrentMonth.EMPTY_MESSAGE',
    defaultMessage: 'Im aktuellen Monat wurden noch keine Kundengespräche geführt.',
  },
});

class BillingCurrentMonth extends Component {
  constructor(props) {
    super(props);
    const savedState = localStorage.getItem('BillingCurrentMonthTableState');
    const defaultState = {
      sortColumn: 'begin',
      sortDirection: 'desc',
      columnWidths: {
        col0: 150, // Beginn
        col1: 200, // Benutzer
        col2: 200, // Teilnehmername
        col3: 150, // Teilnehmer-TelNr
        col4: 200, // Projekt
      }
    };
    
    this.state = savedState ? { ...defaultState, ...JSON.parse(savedState) } : defaultState;
    this.resizingColumn = null;
    this.startX = 0;
    this.startWidth = 0;
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  saveState = () => {
    const { sortColumn, sortDirection, columnWidths } = this.state;
    localStorage.setItem('BillingCurrentMonthTableState', JSON.stringify({ sortColumn, sortDirection, columnWidths }));
  }

  toggleSort = (column) => {
    this.setState(prevState => {
      let nextDir = 'asc';
      if (prevState.sortColumn === column) {
        nextDir = prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      }
      return {
        sortColumn: column,
        sortDirection: nextDir
      };
    }, this.saveState);
  }

  handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    this.resizingColumn = columnKey;
    this.startX = e.pageX;
    this.startWidth = this.state.columnWidths[columnKey];
    
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizeMove = (e) => {
    if (!this.resizingColumn) return;
    const diff = e.pageX - this.startX;
    const newWidth = Math.max(50, this.startWidth + diff);
    this.setState(prevState => ({
      columnWidths: {
        ...prevState.columnWidths,
        [this.resizingColumn]: newWidth
      }
    }));
  };

  handleResizeEnd = () => {
    this.resizingColumn = null;
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    this.saveState();
  };

  renderSortIcon = (column) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return <i className="fa fa-sort" style={{ marginLeft: '8px', color: 'var(--text-muted)', opacity: 0.3 }} />;
    }
    return <i className={`fa ${sortDirection === 'asc' ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc'}`} style={{ marginLeft: '8px', color: 'var(--text-color)' }} />;
  }

  render() {
    const { connections, filter, intl: { formatMessage } } = this.props;

    let filteredItems = connections || [];
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      filteredItems = filteredItems.filter(c => {
        return (c.user && c.user.toLowerCase().includes(lowerFilter)) ||
               (c.customerName && c.customerName.toLowerCase().includes(lowerFilter)) ||
               (c.customerPhone && c.customerPhone.toLowerCase().includes(lowerFilter)) ||
               (c.order && c.order.toLowerCase().includes(lowerFilter));
      });
    }

    if (filteredItems.length === 0) {
      return <p style={{ color: 'var(--text-muted)', padding: '24px', textAlign: 'center' }}>{formatMessage(messages.EMPTY_MESSAGE)}</p>;
    }

    const { sortColumn, sortDirection, columnWidths } = this.state;
    
    const sortedItems = [...filteredItems].sort((a, b) => {
      if (!sortColumn) return 0;
      
      let valA, valB;
      if (sortColumn === 'begin') {
        valA = a.begin; valB = b.begin;
      } else if (sortColumn === 'user') {
        valA = (a.user || '').toLowerCase(); valB = (b.user || '').toLowerCase();
      } else if (sortColumn === 'customerName') {
        valA = (a.customerName || '').toLowerCase(); valB = (b.customerName || '').toLowerCase();
      } else if (sortColumn === 'customerPhone') {
        valA = (a.customerPhone || '').toLowerCase(); valB = (b.customerPhone || '').toLowerCase();
      } else if (sortColumn === 'order') {
        valA = (a.order || '').toLowerCase(); valB = (b.order || '').toLowerCase();
      } else if (sortColumn === 'duration') {
        valA = a.duration || 0; valB = b.duration || 0;
      }
      
      if (valA === valB) return 0;
      if (valA === undefined || valA === null) return sortDirection === 'asc' ? 1 : -1;
      if (valB === undefined || valB === null) return sortDirection === 'asc' ? -1 : 1;
      
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      return sortDirection === 'asc' ? 1 : -1;
    });

    let totalDuration = 0;

    const thBaseStyle = {
      padding: '16px 24px',
      color: 'var(--text-muted)',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      position: 'relative',
      cursor: 'pointer',
      userSelect: 'none'
    };

    const getThStyle = (key) => ({
      ...thBaseStyle,
      width: `${columnWidths[key]}px`,
      minWidth: `${columnWidths[key]}px`,
      maxWidth: `${columnWidths[key]}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    });

    const resizerStyle = {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      zIndex: 1,
    };

    const renderResizer = (key) => (
      <div 
        style={resizerStyle} 
        onMouseDown={(e) => this.handleResizeStart(e, key)}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--text-muted)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      />
    );

    return (
      <div style={{ width: '100%', overflowX: 'auto', backgroundColor: 'var(--panel-bg)', borderRadius: '8px', border: '1px solid var(--panel-border)' }}>
        <table style={{ minWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--panel-bg)', borderBottom: '1px solid var(--panel-border)' }}>
              <th onClick={() => this.toggleSort('begin')} style={getThStyle('col0')}>
                {formatMessage(messages.CALL_BEGIN)}
                {this.renderSortIcon('begin')}
                {renderResizer('col0')}
              </th>
              <th onClick={() => this.toggleSort('user')} style={getThStyle('col1')}>
                {formatMessage(messages.USER_NAME)}
                {this.renderSortIcon('user')}
                {renderResizer('col1')}
              </th>
              <th onClick={() => this.toggleSort('customerName')} style={getThStyle('col2')}>
                {formatMessage(messages.CUSTOMER_NAME)}
                {this.renderSortIcon('customerName')}
                {renderResizer('col2')}
              </th>
              <th onClick={() => this.toggleSort('customerPhone')} style={getThStyle('col3')}>
                {formatMessage(messages.CUSTOMER_PHONE)}
                {this.renderSortIcon('customerPhone')}
                {renderResizer('col3')}
              </th>
              <th onClick={() => this.toggleSort('order')} style={getThStyle('col4')}>
                {formatMessage(messages.PROJECT)}
                {this.renderSortIcon('order')}
                {renderResizer('col4')}
              </th>
              <th onClick={() => this.toggleSort('duration')} style={{ ...thBaseStyle, width: 'auto', textAlign: 'right' }}>
                {formatMessage(messages.DURATION)}
                {this.renderSortIcon('duration')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((connection, idx) => {
              if (connection.duration === 0 && !connection.active) {
                return null;
              }

              totalDuration += connection.duration;

              return (
                <tr key={idx} style={{ borderBottom: '1px solid var(--panel-border)', transition: 'background-color 0.2s', ':hover': { backgroundColor: 'var(--btn-default-hover-bg)' } }}>
                  <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {connection.begin}
                    {connection.active &&
                      <i
                        className="icon-phone"
                        style={{ color: '#4db88a', marginLeft: 8 }}
                        title={formatMessage(messages.CALL_ACTIVE)}
                      />}
                  </td>
                  <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {filter ? <Highlight search={filter}>{trunc(connection.user, 30)}</Highlight> : trunc(connection.user, 30)}
                  </td>
                  <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {filter ? <Highlight search={filter}>{trunc(connection.customerName, 30)}</Highlight> : trunc(connection.customerName, 30)}
                  </td>
                  <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {filter ? <Highlight search={filter}>{connection.customerPhone}</Highlight> : connection.customerPhone}
                  </td>
                  <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {filter ? <Highlight search={filter}>{trunc(connection.order, 30)}</Highlight> : trunc(connection.order, 30)}
                  </td>
                  <td style={{ padding: '16px 24px', textAlign: 'right' }}>{connection.duration}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: 'rgba(0,0,0,0.1)', borderTop: '2px solid var(--panel-border)' }}>
              <td colSpan="5" style={{ padding: '16px 24px', textAlign: 'right', fontWeight: 'bold' }}>
                {formatMessage(messages.TOTAL_DURATION)}
              </td>
              <td style={{ padding: '16px 24px', textAlign: 'right', fontWeight: 'bold' }}>
                {totalDuration}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }
}

export default injectIntl(BillingCurrentMonth);
